import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    ICustomerPortalService,
    NewCardForm,
    PartyPropertyEnum,
    PolicyPropertyEnum,
    ProductsEnum,
    Types
} from "@falcon/services";
import {FailedPaymentMessage} from "./FailedPaymentMessage";
import {Checkbox} from "../inputs/Checkbox";
import {getProductByPolicyNumber, setSessionStorage} from "../../CommonFunctions";

interface IProps {
    newCardForm?: NewCardForm,
    verifyNewCardFailed: boolean,
    policyNumber?: string
}

interface IState {
    addressConfirmationErrorVisible: boolean,
    addressConfirmed: boolean,
    isBillingAddressChangeAllowed: boolean,
    reuseCard: boolean,
    directDebitSupported: boolean
}

@authorize
export class CardForm extends React.Component<IProps, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IProps) {
        super(props);

        this.state = {
            addressConfirmationErrorVisible: false,
            addressConfirmed: false,
            isBillingAddressChangeAllowed: false,
            reuseCard: false,
            directDebitSupported: false
        };

        this.onAddressConfirmChange = this.onAddressConfirmChange.bind(this);
        this.blockerClick = this.blockerClick.bind(this);
        this.onCardStorageConsentChange = this.onCardStorageConsentChange.bind(this);
    }

    public componentDidMount() {
        setSessionStorage("reuseCard", false);
        this.customerPortalService.getPartyPropertyByPropertyKey(PartyPropertyEnum.BILLING_ADDRESS_CHANGE_ALLOWED)
            .subscribe(partyProperty => {
                this.setState({
                    isBillingAddressChangeAllowed: partyProperty.value === "true"
                });
            });

        if (this.props.policyNumber) {
            if (getProductByPolicyNumber(this.props.policyNumber) === ProductsEnum.HOME) {
                this.setState({
                    directDebitSupported: true
                });
            } else {
                this.customerPortalService.getPolicyPropertyByPropertyKey(this.props.policyNumber, PolicyPropertyEnum.DIRECT_DEBIT_SUPPORTED)
                    .subscribe(partyProperty => {
                        this.setState({
                            directDebitSupported: partyProperty?.value === "true"
                        });
                    });
            }
        }
    }

    public render() {

        if (this.props.newCardForm) {

            const address = this.props.newCardForm.address;
            return (
                <React.Fragment>
                    {this.props.verifyNewCardFailed && <FailedPaymentMessage directDebitSupported={this.state.directDebitSupported}/>}

                    <div className="panel payment-iframe-wrapper p-0 payment-header-wdg">
                        <div className="row no-gutters payment-header-row secure-payment-header">
                            <h3>Your payment is secure</h3>
                        </div>
                        <div className="row no-gutters payment-header-row secure-payment-body">
                            <p>We use Verified by Visa and MasterCard SecureCode to make sure your payment details are protected.</p>
                            <div className="card-icon-wrapper">
                                <img className="visa-icon" alt="visa-icon" src="/img/visa-icon.svg"/>
                                <img className="mastercard-icon" alt="mastercard-icon" src="/img/mastercard-icon.svg"/>
                            </div>
                        </div>
                        {this.state.isBillingAddressChangeAllowed && <Checkbox
                            className="mt-3 ml-3"
                            label="I consent to my card details being stored securely for any future policy changes I make."
                            name="cardStorageConsentCheckbox"
                            id="cardStorageConsentCheckbox"
                            value={this.state.reuseCard}
                            onChanged={this.onCardStorageConsentChange}
                        />}
                        <div className="row no-gutters payment-header-row">
                        {!this.state.isBillingAddressChangeAllowed && (
                            <div className="col-12">
                                {address && (
                                    <>
                                        <b>You told us that your Allianz account holder address is:</b>
                                        <div id="address-display" style={{marginTop: "10px", marginBottom: "10px"}}>
                                            <div
                                                id="address-line-1">{[address.subBuildingName, address.buildingName].filter(x => x).join(" ")}</div>
                                            <div
                                                id="address-line-2">{[address.buildingNumber, address.dependentThoroughfareName, address.dependentThoroughfareDescriptor, address.thoroughfareName, address.thoroughfareDescriptor].filter(x => x).join(" ")}</div>
                                            <div
                                                id="address-line-3">{[address.doubleDependentLocality, address.dependentLocality, address.postTown].filter(x => x).join(" ")}</div>
                                            <div
                                                id="address-line-4">{[address.outwardPostcode, address.inwardPostcode].filter(x => x).join(" ")}</div>
                                        </div>
                                    </>
                                )}
                                <div className="falcon-checkbox form-group mt-3">
                                    <div className="d-flex">
                                        <div className="form-check mr-3">
                                            <input className="custom-control-input true"
                                                   onChange={this.onAddressConfirmChange}
                                                   type="checkbox"
                                                   aria-label="I confirm that my card is registered to the above address"
                                                   name={window.name}
                                                   id="payment-address-confirm"
                                                   defaultChecked={this.state.addressConfirmed}/>
                                            <label className="custom-control-label" htmlFor="payment-address-confirm">
                                                <div className="checkbox-big"/>
                                                <div className="checkbox-label">
                                                    <p className="">I confirm that my card is registered to the above
                                                        address</p>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                    {this.state.addressConfirmationErrorVisible && (
                                        <div className="invalid-feedback d-block" role="alert">To proceed you must
                                            confirm</div>
                                    )}
                                </div>
                                <Checkbox
                                    className="mt-3 ml-3"
                                    label="I consent to my card details being stored securely for any future policy changes I make."
                                    name="cardStorageConsentCheckbox"
                                    id="cardStorageConsentCheckbox"
                                    value={this.state.reuseCard}
                                    onChanged={this.onCardStorageConsentChange}
                                />
                                <p>We can only accept payment from a card registered to this address</p>
                                <p>If you don't have one, please update your details with your card issuer. </p>
                                <p>If you attempt the payment using a card registered to a different address, your funds
                                    may result in a pending transaction and your Allianz insurance subscription won’t be
                                    set up.</p>
                            </div>
                        )}
                        </div>
                        <div className="row no-gutters inner-iframe-wrapper payment-gateway-iframe-blockable">
                            {!this.state.isBillingAddressChangeAllowed && (
                                <div onClick={() => {
                                }} onKeyDown={this.blockerClick} className="payment-gateway-iframe-blocker"
                                     style={{display: this.state.addressConfirmed ? "none" : "block"}} role="alert"
                                     aria-hidden/>
                            )}
                            <iframe
                                id="payment-gateway-iframe"
                                src={this.props.newCardForm.iFrameUrl}
                                className="payment-iframe"
                                frameBorder="0"
                                tabIndex={this.state.addressConfirmed ? 0 : -1}
                            />
                        </div>
                    </div>
                </React.Fragment>
            );
        } else {
            return <Overlay show={true}/>
        }
    }

    private onAddressConfirmChange(event: any) {
        this.setState({
            addressConfirmed: event.target.checked,
            addressConfirmationErrorVisible: !event.target.checked
        })
    }

    private blockerClick() {
        this.setState({
            addressConfirmationErrorVisible: true
        });
        document.getElementById("payment-address-confirm")!.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest"
        });
    }

    private onCardStorageConsentChange(value: boolean) {
        this.setState({
            reuseCard: value
        });
        setSessionStorage("reuseCard", value);
    }
}



// WEBPACK FOOTER //
// ./src/components/payments/CardForm.tsx